import { useState, useEffect } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { 
  Brain, BarChart3, Target, Zap, Lock, CheckCircle2, 
  Clock, Star, TrendingUp, ChevronRight, Sparkles,
  Trophy, Flame, ArrowLeft, Users, Play, BookOpen,
  Layers, Map, Gamepad2, ChevronDown, XCircle, Award, Search
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import BadgesDisplay from '../components/BadgesDisplay';
import BeatTheDataLeaderboard from '../components/BeatTheDataLeaderboard';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

// Helper to determine the best dataKey for visualization
const getChartDataKey = (data) => {
  if (!data || data.length === 0) return 'clue_count';
  const sample = data[0];
  // Priority: percentage > contestant_accuracy > clue_count > count > total
  if (sample.percentage !== undefined) return 'percentage';
  if (sample.contestant_accuracy !== undefined) return 'contestant_accuracy';
  if (sample.clue_count !== undefined) return 'clue_count';
  if (sample.count !== undefined) return 'count';
  if (sample.total !== undefined) return 'total';
  return 'clue_count';
};

// Helper to get the label field (category, answer, or _id)
const getChartLabelKey = (data) => {
  if (!data || data.length === 0) return 'category';
  const sample = data[0];
  if (sample.category !== undefined) return 'category';
  if (sample.answer !== undefined) return 'answer';
  if (sample._id !== undefined) return '_id';
  return 'category';
};

// Helper to format chart data for display
const formatChartData = (data, dataKey) => {
  if (!data) return [];
  const labelKey = getChartLabelKey(data);
  return data.map(item => ({
    ...item,
    // Normalize the label field to 'category' for consistent rendering
    category: item[labelKey] || item.category || item.answer || item._id || 'Unknown',
    // Handle contestant_accuracy - check if it's already a percentage (> 1) or decimal (< 1)
    displayValue: dataKey === 'contestant_accuracy' 
      ? (item.contestant_accuracy > 1 ? Math.round(item.contestant_accuracy) : Math.round(item.contestant_accuracy * 100))
      : item[dataKey]
  }));
};

const CATEGORY_ICONS = {
  frequency: BarChart3,
  difficulty: Target,
  trends: TrendingUp,
  strategy: Zap,
  comparison: Brain,
  meta: Sparkles,
  patterns: Layers
};

const CATEGORY_COLORS = {
  frequency: 'text-blue-400 bg-blue-500/20',
  difficulty: 'text-red-400 bg-red-500/20',
  trends: 'text-green-400 bg-green-500/20',
  strategy: 'text-purple-400 bg-purple-500/20',
  comparison: 'text-cyan-400 bg-cyan-500/20',
  meta: 'text-yellow-400 bg-yellow-500/20',
  patterns: 'text-pink-400 bg-pink-500/20'
};

export default function AskAlex() {
  useSEO('AskAlex AI Training Hub | JepPrep', 'AI-powered trivia training with missions, data analysis challenges, and personalized study recommendations.');
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [expandedMission, setExpandedMission] = useState(null);
  const [completionData, setCompletionData] = useState({});

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [missionsRes, statsRes, streakRes] = await Promise.all([
        api.get(`/api/v1/askalex/missions?user_id=${user.id}`),
        api.get(`/api/v1/askalex/stats?user_id=${user.id}`),
        api.get(`/api/v1/askalex/predictions/streak?user_id=${user.id}`)
      ]);
      setMissions(missionsRes.data);
      setStats(statsRes.data);
      setStreak(streakRes.data);
    } catch (error) {
      console.error('Error fetching AskAlex data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletionData = async (missionId) => {
    // If we already have data for this mission, don't refetch
    if (completionData[missionId]) return;
    
    try {
      const res = await api.get(`/api/v1/askalex/missions/${missionId}/completion?user_id=${user.id}`);
      if (res.data.completed) {
        setCompletionData(prev => ({
          ...prev,
          [missionId]: res.data
        }));
      }
    } catch (error) {
      console.error('Error fetching completion data:', error);
    }
  };

  const handleMissionClick = (mission, e) => {
    if (mission.is_locked) return;
    
    // If mission is completed and clicking on the card (not dropdown), toggle dropdown
    if (mission.is_completed) {
      // Fetch completion data if not already loaded
      fetchCompletionData(mission.mission_id);
      
      // Toggle expanded state
      if (expandedMission === mission.mission_id) {
        setExpandedMission(null);
      } else {
        setExpandedMission(mission.mission_id);
      }
    } else {
      // Navigate to mission
      navigate(`/askalex/mission/${mission.mission_id}`);
    }
  };

  const filteredMissions = missions.filter(m => {
    if (activeTab === 'all') return true;
    if (activeTab === 'completed') return m.is_completed;
    return m.category_tag === activeTab;
  });

  const completedCount = missions.filter(m => m.is_completed).length;
  const progressPercent = missions.length > 0 ? (completedCount / missions.length) * 100 : 0;

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-emerald-400';
    if (accuracy >= 60) return 'text-yellow-300';
    if (accuracy >= 40) return 'text-amber-400';
    return 'text-rose-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading AskAlex...</div>
      </div>
    );
  }

  const accuracy = stats?.total_predictions > 0 
    ? Math.round((stats.correct_predictions / stats.total_predictions) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Header - matching SprintStats */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12 shrink-0" />
              <h1 className="text-lg sm:text-xl font-display text-white hidden sm:block">AskAlex</h1>
            </div>
            <Link to="/dashboard">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-sm shrink-0">
                <Brain className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Dashboard</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Cards - matching SprintStats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Trophy className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className="text-3xl font-bold text-white">{stats?.level || 1}</p>
              <p className="text-gray-400 text-sm">Level</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="text-3xl font-bold text-white">{completedCount}/{missions.length}</p>
              <p className="text-gray-400 text-sm">Missions</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Flame className="w-8 h-8 mx-auto mb-2 text-orange-400" />
              <p className="text-3xl font-bold text-white">{streak?.current_streak || 0}</p>
              <p className="text-gray-400 text-sm">Streak</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
            <CardContent className="p-4 text-center">
              <Target className="w-8 h-8 mx-auto mb-2 text-[hsl(var(--brand-gold-500))]" />
              <p className={`text-3xl font-bold ${getAccuracyColor(accuracy)}`}>{accuracy}%</p>
              <p className="text-gray-400 text-sm">Accuracy</p>
            </CardContent>
          </Card>
        </div>

        {/* Beat the Data Feature Card */}
        <Link to="/askalex/beatthedata">
          <Card className="bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border-cyan-500/30 hover:border-cyan-500/50 transition-all cursor-pointer mb-8" data-testid="beat-the-data-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-bold text-base sm:text-lg">Beat the Data</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">Answer paraphrased clues and outperform historical contestants</p>
                  </div>
                </div>
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white shrink-0 w-full sm:w-auto">
                  <Play className="w-4 h-4 mr-2" />
                  Play Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Flash Cards Feature Card */}
        <Link to="/askalex/flashcards">
          <Card className="bg-gradient-to-r from-amber-600/20 to-yellow-600/20 border-amber-500/30 hover:border-amber-500/50 transition-all cursor-pointer mb-8" data-testid="flash-cards-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center shrink-0">
                    <Zap className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-bold text-base sm:text-lg">Flash Cards</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">Rapid-fire clue practice — 20 random cards, no clock, just you and the answers</p>
                  </div>
                </div>
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white shrink-0 w-full sm:w-auto">
                  <Zap className="w-4 h-4 mr-2" />
                  Start
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Clue Explorer Feature Card */}
        <Link to="/askalex/explore">
          <Card className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border-emerald-500/30 hover:border-emerald-500/50 transition-all cursor-pointer mb-8" data-testid="clue-explorer-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
                    <Search className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-bold text-base sm:text-lg">Clue Explorer</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">Search 514K+ clues, discover patterns, and find categories to practice</p>
                  </div>
                </div>
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white shrink-0 w-full sm:w-auto">
                  <Search className="w-4 h-4 mr-2" />
                  Explore
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Knowledge Portfolio Feature Card */}
        <Link to="/askalex/portfolio">
          <Card className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-purple-500/30 hover:border-purple-500/50 transition-all cursor-pointer mb-8" data-testid="knowledge-portfolio-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shrink-0">
                    <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-bold text-base sm:text-lg">Knowledge Portfolio</h3>
                    <p className="text-gray-400 text-xs sm:text-sm">Track your strengths, weaknesses, and get AI study recommendations</p>
                  </div>
                </div>
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white shrink-0 w-full sm:w-auto">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Portfolio
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Missions Section Card */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardContent className="p-6">
            {/* Section Header */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Brain className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Data Missions</h3>
                <p className="text-gray-400 text-sm">Explore Jeopardy! statistics through guided predictions</p>
              </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
              <TabsList className="bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))] flex flex-wrap">
                <TabsTrigger 
                  value="all" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  All
                </TabsTrigger>
                <TabsTrigger 
                  value="frequency" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Frequency
                </TabsTrigger>
                <TabsTrigger 
                  value="difficulty" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Difficulty
                </TabsTrigger>
                <TabsTrigger 
                  value="strategy" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Strategy
                </TabsTrigger>
                <TabsTrigger 
                  value="trends" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Trends
                </TabsTrigger>
                <TabsTrigger 
                  value="patterns" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Patterns
                </TabsTrigger>
                <TabsTrigger 
                  value="completed" 
                  className="data-[state=active]:bg-[hsl(var(--brand-gold-500))] data-[state=active]:text-[hsl(var(--brand-blue-900))]"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Completed
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Mission Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {filteredMissions.map((mission) => {
                const CategoryIcon = CATEGORY_ICONS[mission.category_tag] || Brain;
                const colorClass = CATEGORY_COLORS[mission.category_tag] || 'text-gray-400 bg-gray-500/20';
                const isExpanded = expandedMission === mission.mission_id;
                const missionCompletion = completionData[mission.mission_id];
                const isPerfectScore = missionCompletion?.correct_predictions === missionCompletion?.total_predictions;
                
                return (
                  <Card 
                    key={mission.mission_id}
                    className={`bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] transition-all duration-200 ${
                      mission.is_locked 
                        ? 'opacity-60 cursor-not-allowed' 
                        : 'hover:border-[hsl(var(--brand-gold-500))]/50 cursor-pointer'
                    }`}
                    onClick={(e) => handleMissionClick(mission, e)}
                    data-testid={`mission-card-${mission.mission_id}`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 ${colorClass.split(' ')[1]}`}>
                            {mission.is_locked ? (
                              <Lock className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                            ) : mission.is_completed ? (
                              <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                            ) : (
                              <CategoryIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${colorClass.split(' ')[0]}`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h3 className="text-white font-semibold mb-1 text-sm sm:text-base">{mission.title}</h3>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className="text-[hsl(var(--brand-gold-500))] font-medium text-xs sm:text-sm whitespace-nowrap">
                                  +{mission.completion_xp} XP
                                </span>
                                {!mission.is_locked && !mission.is_completed && (
                                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                )}
                                {mission.is_completed && (
                                  <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                )}
                              </div>
                            </div>
                            <p className="text-gray-400 text-xs sm:text-sm mb-3">{mission.subtitle}</p>
                            
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs">
                              <span className="flex items-center gap-1 text-gray-500">
                                <Clock className="w-3 h-3" />
                                {mission.estimated_minutes} min
                              </span>
                              <span className="flex items-center gap-1 text-gray-500">
                                {[...Array(mission.difficulty)].map((_, i) => (
                                  <Star key={`diff-${i}`} className="w-3 h-3 fill-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-gold-500))]" />
                                ))}
                              </span>
                              <Badge className={`${colorClass} text-xs`}>
                                {mission.category_tag}
                              </Badge>
                            </div>
                          </div>
                      </div>
                      
                      {/* Completed Mission Stats Dropdown */}
                      {mission.is_completed && isExpanded && missionCompletion && isPerfectScore && (
                        <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]" onClick={(e) => e.stopPropagation()}>
                          {/* Score Summary */}
                          <div className="grid grid-cols-3 gap-3 mb-4">
                            <div className="bg-[hsl(var(--secondary))] rounded-lg p-3 text-center">
                              <p className="text-[hsl(var(--brand-gold-500))] text-lg font-bold">{missionCompletion.total_points}</p>
                              <p className="text-gray-400 text-xs">Points</p>
                            </div>
                            <div className="bg-[hsl(var(--secondary))] rounded-lg p-3 text-center">
                              <p className="text-emerald-400 text-lg font-bold">{missionCompletion.correct_predictions}/{missionCompletion.total_predictions}</p>
                              <p className="text-gray-400 text-xs">Correct</p>
                            </div>
                            <div className="bg-[hsl(var(--secondary))] rounded-lg p-3 text-center">
                              <p className="text-cyan-400 text-lg font-bold">+{missionCompletion.xp_earned}</p>
                              <p className="text-gray-400 text-xs">XP</p>
                            </div>
                          </div>
                          
                          {/* Visualization Chart */}
                          {missionCompletion.visualization_data && (() => {
                            const vizData = missionCompletion.visualization_data.data?.slice(0, 8) || [];
                            const dataKey = getChartDataKey(vizData);
                            const chartData = formatChartData(vizData, dataKey);
                            
                            return (
                            <div className="mb-4">
                              <h4 className="text-white font-medium text-sm mb-3 flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                                {missionCompletion.visualization_data.visualization?.title || 'Data Insights'}
                              </h4>
                              <div className="h-48 bg-[hsl(var(--secondary))] rounded-lg p-2">
                                <ResponsiveContainer width="100%" height="100%">
                                  <BarChart
                                    data={chartData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                                  >
                                    <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                                    <YAxis 
                                      type="category" 
                                      dataKey="category" 
                                      tick={{ fill: '#fff', fontSize: 9 }}
                                      width={75}
                                    />
                                    <Tooltip 
                                      contentStyle={{ 
                                        backgroundColor: 'hsl(222, 47%, 15%)', 
                                        border: '1px solid hsl(222, 47%, 25%)',
                                        borderRadius: '8px'
                                      }}
                                      labelStyle={{ color: '#fff' }}
                                      itemStyle={{ color: '#fff' }}
                                      formatter={(value, name) => [
                                        dataKey === 'contestant_accuracy' ? `${value}%` : value,
                                        dataKey === 'contestant_accuracy' ? 'Accuracy' : name
                                      ]}
                                    />
                                    <Bar 
                                      dataKey="displayValue"
                                      radius={[0, 4, 4, 0]}
                                    >
                                      {chartData.map((entry, index) => (
                                        <Cell 
                                          key={`cell-${index}`} 
                                          fill={index === 0 ? 'hsl(45, 93%, 47%)' : index < 3 ? 'hsl(45, 70%, 50%)' : 'hsl(222, 47%, 45%)'}
                                        />
                                      ))}
                                    </Bar>
                                  </BarChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                            );
                          })()}
                          
                          {/* Prediction Results */}
                          {missionCompletion.prediction_results?.length > 0 && (
                            <div>
                              <h4 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
                                <Target className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                                Your Answers
                              </h4>
                              <div className="space-y-2">
                                {missionCompletion.prediction_results.map((result, idx) => (
                                  <div 
                                    key={result.label || `pred-${idx}`}
                                    className={`p-2 rounded-lg text-sm ${
                                      result.is_correct 
                                        ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                        : 'bg-red-500/10 border border-red-500/30'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      {result.is_correct 
                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                                        : <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                      }
                                      <span className="text-gray-300 text-xs flex-1">{result.prompt}</span>
                                      <Badge className={`text-xs ${
                                        result.is_correct 
                                          ? 'bg-emerald-500/20 text-emerald-400' 
                                          : 'bg-red-500/20 text-red-400'
                                      }`}>
                                        +{result.points_earned}
                                      </Badge>
                                    </div>
                                    <div className="mt-1 pl-6 text-xs">
                                      <span className="text-gray-500">Answer: </span>
                                      <span className="text-emerald-400">{result.user_answer}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Play Again Button */}
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/askalex/mission/${mission.mission_id}`);
                            }}
                            className="w-full mt-4 bg-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-[hsl(var(--brand-blue-900))] font-semibold"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Play Again
                          </Button>
                        </div>
                      )}
                      
                      {/* Loading state for completion data */}
                      {mission.is_completed && isExpanded && !missionCompletion && (
                        <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] text-center py-4">
                          <div className="animate-spin w-6 h-6 border-2 border-[hsl(var(--brand-gold-500))] border-t-transparent rounded-full mx-auto"></div>
                          <p className="text-gray-400 text-sm mt-2">Loading stats...</p>
                        </div>
                      )}
                      
                      {/* Not perfect score - show retry prompt */}
                      {mission.is_completed && isExpanded && missionCompletion && !isPerfectScore && (
                        <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]" onClick={(e) => e.stopPropagation()}>
                          <div className="text-center py-4">
                            <p className="text-gray-400 text-sm mb-2">
                              You scored {missionCompletion.correct_predictions}/{missionCompletion.total_predictions}. Get a perfect score to unlock the statistics!
                            </p>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/askalex/mission/${mission.mission_id}`);
                              }}
                              className="bg-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-[hsl(var(--brand-blue-900))] font-semibold"
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Try Again
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {filteredMissions.length === 0 && (
              <div className="py-12 text-center">
                <Brain className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No missions found in this category</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Badges and Leaderboard Row */}
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Badges Card */}
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Award className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                Your Badges
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BadgesDisplay compact={false} />
            </CardContent>
          </Card>

          {/* Leaderboard Card */}
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                Beat the Data Leaderboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BeatTheDataLeaderboard compact={true} />
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
